import { useState } from "react";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { API, COMPANY_TYPES, PRIMARY_CHALLENGES } from "../lib/constants";

const STEPS = [
  "We review your portfolio and current brand lifecycle stage.",
  "Our experts analyze extra-urban growth potential & opportunities.",
  "We schedule a consultation call within 24 hours.",
  "You receive a customized growth strategy with projected ROI.",
];

export default function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    designation: "",
    company: "",
    email: "",
    phone: "",
    company_type: "",
    primary_challenge: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.company) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, form);
      if (res.data?.success) {
        setSubmitted(true);
        toast.success("Request received. Our team will reach out within 24 hours.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-slate-50 border-t border-slate-200" data-testid="lead-form-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">Take Action</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] font-extrabold leading-[1.04] text-slate-900 mb-6">
            Ready to revitalize <br />
            <span className="font-editorial italic font-medium text-slate-700">your mature & tail-end brands?</span>
          </h2>
          <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-10">
            Request a free portfolio analysis. Our experts will identify untapped revenue
            opportunities in your brand portfolio within 24 hours.
          </p>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">
              What Happens Next
            </div>
            <ol className="space-y-3">
              {STEPS.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700">
                  <span className="h-6 w-6 shrink-0 inline-flex items-center justify-center bg-slate-900 text-white text-xs font-bold tnum">
                    {i + 1}
                  </span>
                  <span className="pt-0.5">{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid="lead-form-success"
              className="bg-white border border-slate-200 p-10 md:p-14 text-center"
            >
              <div className="h-14 w-14 mx-auto inline-flex items-center justify-center bg-teal-50 mb-5">
                <Check className="h-7 w-7 text-teal-700" strokeWidth={2} />
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                Thank you, {form.name.split(" ")[0]}.
              </h3>
              <p className="text-slate-600 text-base">
                Your request has been received. A senior member of the Medstry team will reach out
                within 24 hours with the next steps.
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="lead-form"
              className="bg-white border border-slate-200 p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <Field label="Full Name *" testid="field-name">
                <Input
                  data-testid="input-name"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Aarav Mehta"
                  className="h-11 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
                />
              </Field>
              <Field label="Designation" testid="field-designation">
                <Input
                  data-testid="input-designation"
                  value={form.designation}
                  onChange={(e) => update("designation", e.target.value)}
                  placeholder="VP — Marketing"
                  className="h-11 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
                />
              </Field>
              <Field label="Company *" testid="field-company">
                <Input
                  data-testid="input-company"
                  required
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  placeholder="Pharma Co."
                  className="h-11 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
                />
              </Field>
              <Field label="Company Type" testid="field-company-type">
                <Select
                  value={form.company_type}
                  onValueChange={(v) => update("company_type", v)}
                >
                  <SelectTrigger
                    data-testid="select-company-type"
                    className="h-11 rounded-none border-slate-300 focus:ring-[#5B6BD6]"
                  >
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Email *" testid="field-email">
                <Input
                  data-testid="input-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="aarav@pharma.co"
                  className="h-11 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
                />
              </Field>
              <Field label="Phone *" testid="field-phone">
                <Input
                  data-testid="input-phone"
                  required
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+91 98765 43210"
                  className="h-11 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Primary Challenge" testid="field-challenge">
                  <Select
                    value={form.primary_challenge}
                    onValueChange={(v) => update("primary_challenge", v)}
                  >
                    <SelectTrigger
                      data-testid="select-challenge"
                      className="h-11 rounded-none border-slate-300 focus:ring-[#5B6BD6]"
                    >
                      <SelectValue placeholder="Select your challenge" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIMARY_CHALLENGES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Message (optional)" testid="field-message">
                  <Textarea
                    data-testid="input-message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us about your portfolio or specific brands of interest"
                    className="rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  data-testid="lead-form-submit"
                  disabled={loading}
                  className="btn-primary px-7 py-4 text-sm font-semibold w-full md:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    "Request Portfolio Economics Analysis"
                  )}
                </button>
                <p className="text-xs text-slate-500 mt-3">
                  By submitting, you agree to be contacted by Medstry's team. We respect your privacy.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-600 mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
